import React, { useEffect } from "react";
import { collection, query, orderBy, limit, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../context/AuthProvider";
import { toast } from "react-hot-toast";

export const NotificationProvider: React.FC = () => {
  const { user, loading } = useAuth();

  useEffect(() => {
    // ✅ Wait for auth to complete and ensure user exists
    if (loading || !user?.uid) return;

    // Listen to the user's private notification sub-collection
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(1) // Just check the latest one for real-time alerts
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const notif = change.doc.data();
            // Only show if it's fresh (not read) and reasonably new
            if (!notif.read) {
              toast.custom((t) => (
                <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
                  <div className="flex-1 w-0 p-4">
                    <div className="flex items-start">
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {notif.title}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {notif.message}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex border-l border-gray-200">
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ), { duration: 5000 });

              // Mark as read immediately so it doesn't pop again on refresh
              updateDoc(doc(db, "users", user.uid, "notifications", change.doc.id), { read: true })
                .catch((error) => {
                  console.error("Error marking notification as read:", error);
                });
            }
          }
        });
      },
      (error) => {
        // ✅ Handle permission errors gracefully
        console.error("Notification listener error:", error);
        // Don't show error to user - notifications are non-critical
      }
    );

    return () => unsubscribe();
  }, [user, loading]); // ✅ Add loading to dependencies

  return null; // Renderless component
};
